import { useEffect, useRef } from "react";

export function PcTouchpad({ ready, send, move, sensitivity, dragging, setDragging }) {
  const state = useRef({ points: new Map(), multi: false, moved: false, held: false });
  const holdTimer = useRef(null), tapTimer = useRef(null);
  const readyRef = useRef(ready); readyRef.current = ready;
  const centroid = points => {
    const values = [...points.values()];
    return { x: values.reduce((n,p) => n+p.x, 0)/values.length, y: values.reduce((n,p) => n+p.y, 0)/values.length };
  };
  const clearHold = () => { clearTimeout(holdTimer.current); holdTimer.current = null; };
  const reset = () => {
    clearHold(); clearTimeout(tapTimer.current); tapTimer.current = null;
    state.current = { points: new Map(), multi: false, moved: false, held: false };
  };
  useEffect(() => {
    if (!ready) { reset(); setDragging(false); }
    return () => { clearTimeout(holdTimer.current); clearTimeout(tapTimer.current); };
    // The timers belong to this surface and stop when it is hidden or disconnected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);
  const down = event => {
    if (!ready) return;
    event.preventDefault();
    const s = state.current;
    if (!s.points.size) {
      s.started = performance.now(); s.multi = false; s.moved = false; s.held = false;
      s.scrollX = 0; s.scrollY = 0; s.travel = 0;
    }
    s.points.set(event.pointerId, { x:event.clientX, y:event.clientY });
    event.currentTarget.setPointerCapture?.(event.pointerId);
    if (s.points.size === 1 && !dragging) {
      holdTimer.current = setTimeout(() => {
        if (readyRef.current && s.points.size === 1 && !s.moved && send({action:"drag",active:true})) {
          s.held = true; setDragging(true);
        }
      }, 450);
    } else {
      s.multi = true; clearHold();
      // A two-finger gesture must not trigger a delayed single-finger click.
      clearTimeout(tapTimer.current); tapTimer.current = null;
    }
  };
  const motion = event => {
    const s = state.current, point = s.points.get(event.pointerId);
    if (!ready || !point) return;
    event.preventDefault();
    const before = centroid(s.points);
    const dx = event.clientX-point.x, dy = event.clientY-point.y;
    s.travel += Math.hypot(dx,dy);
    if (s.travel > 8) { s.moved = true; clearHold(); clearTimeout(tapTimer.current); tapTimer.current = null; }
    s.points.set(event.pointerId, { x:event.clientX, y:event.clientY });
    if (s.points.size > 1) {
      const after = centroid(s.points);
      s.scrollX += after.x-before.x; s.scrollY += after.y-before.y;
      const sx = Math.trunc(s.scrollX/18), sy = Math.trunc(s.scrollY/18);
      if (sx || sy) {
        const boundedX = Math.max(-10,Math.min(10,sx)), boundedY = Math.max(-10,Math.min(10,sy));
        if (send({action:"scroll",dx:boundedX,dy:-boundedY})) {
          s.scrollX -= boundedX*18; s.scrollY -= boundedY*18;
        }
      }
    } else if (!s.multi) move(dx*sensitivity,dy*sensitivity);
  };
  const up = event => {
    const s = state.current;
    if (!s.points.has(event.pointerId)) return;
    event.preventDefault(); s.points.delete(event.pointerId); clearHold();
    if (s.points.size) return;
    if (s.held) { send({action:"drag",active:false}); setDragging(false); }
    else if (!s.moved && !dragging && performance.now()-s.started < 400) {
      if (s.multi) { clearTimeout(tapTimer.current); tapTimer.current=null; send({action:"click",button:"right"}); }
      else if (tapTimer.current) {
        clearTimeout(tapTimer.current); tapTimer.current=null;
        send({action:"click",button:"left",count:2});
      } else tapTimer.current = setTimeout(() => {
        tapTimer.current=null;
        if (readyRef.current) send({action:"click",button:"left"});
      },220);
    }
  };
  const cancel = event => {
    if (!state.current.points.has(event.pointerId)) return;
    reset(); send({action:"release"}); setDragging(false);
  };
  return <div className={"pc-touchpad"+(dragging?" is-dragging":"")} role="application" aria-label="Touchpad"
    onPointerDown={down} onPointerMove={motion} onPointerUp={up} onPointerCancel={cancel}
    onLostPointerCapture={cancel} onContextMenu={e=>e.preventDefault()}>
    <div className="pc-touchpad-guide" aria-hidden="true">
      <svg viewBox="0 0 48 48" fill="none"><path d="M17 24V10a3 3 0 0 1 6 0v13-8a3 3 0 0 1 6 0v10-6a3 3 0 0 1 6 0v9-4a3 3 0 0 1 6 0v10c0 7-5 11-12 11-6 0-9-3-12-7l-7-9c-3-4 1-7 4-4l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
      <strong>{dragging?"ARRASTRANDO":"TOUCHPAD"}</strong>
      <span>{dragging?"Deslizá para mover · Soltá para terminar":"Deslizá un dedo para mover el cursor"}</span>
      <small>Toque: clic · Doble toque: doble clic<br/>Dos dedos: scroll / clic derecho · Mantené: arrastrar</small>
    </div>
    <div className="pc-scroll-rail" aria-hidden="true">SCROLL ↕</div>
  </div>;
}
